import {
    ActivateProductCcpResponse,
    ActivateProductCofsResponse
} from "@/libraries/bxp-web-stargate-client/src/hook";

import {
    getIdentityBaseUrl,
    getWacIntegrationUrl,
    getRequestId,
    Bundle,
    ProductKey,
    isProduction,
    ProductData,
    ProductDataDetail
} from "./";

/**
 * Returns continue param for redirect after login if present and valid
 *
 * @param location {Location} - current page location
 *
 * @returns {string} - continue URL
 */
export function getContinueParam(location: Location): string | null {
    const url = new URL(location.href);
    const continueParam = url.searchParams.get("continue");
    if (continueParam) {
        try {
            const continueUrl = new URL(continueParam);
            if (continueUrl.origin === getWacIntegrationUrl()) {
                // only use continue param if it's a valid WAC URL
                return decodeURIComponent(continueParam);
            }
        } catch (e) {
            // continue param is not a valid URL
        }
    }
    return null;
}

/**
 * Returns constructed subdirectory for URL path based on locale
 *
 * @param locale {string} - locale string from router
 *
 * @returns {string} - URL friendly locale subdirectory
 */
export function getLocalePath(locale?: string): string {
    return (locale || "en") === "en" ? "" : `/${locale}`;
}

/**
 * Returns login URL for existing product
 *
 * @param location {Location} - current page location
 *
 * @returns {string} - login URL as a string
 */
export const getLoginUrl = (location: Location): string => {
    const loginUrl = new URL("/login", getIdentityBaseUrl());
    const continueParam = getContinueParam(location);
    if (continueParam) {
        loginUrl.searchParams.append("continue", continueParam);
    } else {
        const defaultContinueUrl = new URL(
            "/gateway/api/start/authredirect",
            getWacIntegrationUrl()
        ).toString();
        loginUrl.searchParams.append("continue", defaultContinueUrl);
    }
    return loginUrl.toString();
};

/**
 * Returns confirmation page redirect URL,
 * includes existing parameters & adds a specified `product` param
 *
 * @param baseUrl {string} - base URL for WAC
 * @param localePath {string} - locale string for url
 *
 * @returns {string} - confirmation page redirect URL
 */
export const getNameYourSiteRedirectUrl = ({
    bundle,
    edition,
    application,
    localePath = "",
    templateId
}: {
    bundle?: string;
    edition?: string;
    localePath?: string;
    application?: string;
    templateId?: string;
}): string => {
    const baseUrl = getWacIntegrationUrl();
    const siteNamePageUrl = "/try/cloud/signup";
    const searchParameters = new URLSearchParams(window.location.search);

    const platformParam = searchParameters.get("platform");
    if (!templateId) {
        templateId = searchParameters.get("templateId") ?? undefined;
    }
    const continueParam = searchParameters.get("continue");
    const team_type = searchParameters.get("team_type");
    const publicLinkId = searchParameters.get("publicLinkId");

    const params = new URLSearchParams();

    // bundle first
    if (bundle) {
        params.append("bundle", bundle);
    }

    // edition comes after bundle
    if (edition) {
        params.append("edition", edition);
    }

    if (platformParam) {
        params.append("platform", platformParam);
    }

    if (continueParam) {
        params.append("continue", continueParam);
    }

    if (templateId) {
        params.append("templateId", templateId);
    }

    if (team_type) {
        params.append("team_type", team_type);
    }

    if (publicLinkId) {
        params.append("publicLinkId", publicLinkId);
    }

    if (application) {
        params.append("application", application);
    }

    return `${baseUrl}${localePath}${siteNamePageUrl}?${params}`;
};

/**
 * Returns confirmation page URL
 *
 * @param locale {string} - optional language locale for url (example /fr/ondemand/signup/confirmation)
 * @param productKey {string} - product key
 * @param siteName {string} - site name
 * @param signupId {string} - id used to track the e2e signup
 * @param response {object} - response object from product activation
 *
 * @returns {string} - confirmation page URL
 */
export function getConfirmationPageUrl({
    locale,
    productKey,
    siteName,
    signupId,
    response,
    edition
}: {
    locale?: string;
    productKey: string;
    siteName: string;
    signupId?: string;
    edition: string;
    response: ActivateProductCcpResponse | ActivateProductCofsResponse;
}) {
    // required query parameters from activation
    const redirectParams = new URLSearchParams();
    redirectParams.append("ondemandurl", siteName);
    redirectParams.append("products", productKey);
    redirectParams.append("edition", edition);
    if (response.orchestrationId) {
        redirectParams.append("orchestrationId", response.orchestrationId);
    }

    if (signupId) {
        redirectParams.append("signupId", signupId);
    }

    if (response.service === "ccp") {
        const ccpResponse = response as ActivateProductCcpResponse;
        const requestId = ccpResponse.metadata?.provisionRequestId;
        redirectParams.append("platform", "ccp");
        redirectParams.append("provisioningRequestId", requestId);
    } else {
        const cofsResponse = response as ActivateProductCofsResponse;
        const requestId = getRequestId(cofsResponse.progressUri);
        redirectParams.append("cloudId", cofsResponse.cloudId);
        if (requestId) {
            redirectParams.append("requestId", requestId);
        }
    }

    // pass through current query parameters
    const searchParameters = new URLSearchParams(window.location.search);

    const n2n = searchParameters.get("n2n") === "true";
    if (n2n) {
        redirectParams.append("n2n", "true");
    }

    const signupSource = searchParameters.get("signupSource");
    if (signupSource) {
        redirectParams.append("signupSource", signupSource);
    }

    const team_type = searchParameters.get("team_type");
    if (team_type) {
        redirectParams.append("team_type", team_type);
    }

    const templateId = searchParameters.get("templateId");
    if (templateId) {
        redirectParams.append("templateId", templateId);
    }

    const localePath = getLocalePath(locale);
    return `${localePath}/ondemand/signup/confirmation?${redirectParams}`;
}

/**
 * Returns primary (first) product key from bundle
 *
 * @param {Bundle} bundle - product bundle
 *
 * @returns {string} - product key
 */
export const getPrimaryProductKeyFromBundle = (bundle: Bundle) => {
    switch (bundle) {
        case "confluence":
            return ProductKey.CONFLUENCE;
        case "jira-service-management":
        case "jira-service-management-jira-software":
            return ProductKey.JIRA_SERVICE_MANAGEMENT;
        case "devai":
            return ProductKey.DEVAI;
        default:
            return ProductKey.JIRA_SOFTWARE;
    }
};

/**
 * Returns product data from bundle
 *
 * @param bundle {string} - product bundle
 *
 * @returns {ProductDataDetail[]} - product key
 */
export const getProductsFromBundle = (bundle: Bundle): ProductDataDetail[] => {
    switch (bundle) {
        case "confluence":
            return [ProductData[ProductKey.CONFLUENCE]];
        case "jira-service-management":
            return [ProductData[ProductKey.JIRA_SERVICE_MANAGEMENT]];
        case "jira-service-management-jira-software":
            return [
                ProductData[ProductKey.JIRA_SERVICE_MANAGEMENT],
                ProductData[ProductKey.JIRA_SOFTWARE]
            ];
        case "devai":
            return [ProductData[ProductKey.DEVAI]];
        default:
            return [ProductData[ProductKey.JIRA_SOFTWARE]];
    }
};

/**
 * Returns URL to switch account page
 *
 * @returns {string} - URL
 */
export const getSwitchAccountUrl = (): string => {
    const params = new URLSearchParams({
        continue: window.location.href
    });
    return `${getIdentityBaseUrl()}/login/select-account?${params}`;
};

/**
 * Returns URL to add product to site via crossflow
 *
 * @returns {string} - URL
 */
export const getCrossFlowActivationUrl = ({
    cloudId,
    edition,
    productKey,
    siteName
}: {
    cloudId: string;
    edition?: string;
    productKey: string;
    siteName: string;
}) => {
    const startUrl = new URL(
        isProduction()
            ? "https://start.atlassian.com"
            : "https://start.stg.atlassian.com"
    );
    startUrl.searchParams.set("startTrialProduct", productKey);
    startUrl.searchParams.set("startTrialCloudId", cloudId);
    startUrl.searchParams.set("startTrialInstance", siteName);
    if (edition) {
        startUrl.searchParams.set("edition", edition);
    }

    // pass through current query parameters
    const searchParameters = new URLSearchParams(window.location.search);

    const templateId = searchParameters.get("templateId");
    if (templateId) {
        startUrl.searchParams.set("templateId", templateId);
    }

    const billingCode = searchParameters.get("billingMode");
    if (billingCode) {
        startUrl.searchParams.set("billingMode", billingCode);
    }

    const billingMode = searchParameters.get("billingMode");
    if (billingMode) {
        startUrl.searchParams.set("billingMode", billingMode);
    }

    return startUrl.toString();
};
